/**
 * EmployeeOnboardingWizard — LBR2-C3 (2026-07-26)
 *
 * Multi-step onboarding wizard that creates a linked User + Employee (via the
 * backend `POST /employees/onboard` → C1's createLinkedEmployeeUser), captures
 * the rich KYC fields, and uploads KYC documents (PAN/Aadhaar/photo) through the
 * shared `/api/v1/files` service, attaching each as an EmployeeKycDocument.
 */
import { useEffect, useState } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { uploadKycDocs } from './kycUpload';
import { X, Loader2, ChevronLeft, ChevronRight, Check, Upload, Trash2 } from 'lucide-react';
import { api } from '@/utils/api';
import {
  ONBOARDING_STEPS,
  emptyOnboardingForm,
  validateStep,
  canSubmit,
  kycDocTypeLabel,
  KYC_DOC_TYPES,
  type OnboardingForm,
  type OnboardingStepId,
  type KycDocType,
  type WizardMode,
} from './employeeOnboardingSteps';
import { placeholderEmailFor, isPlaceholderEmail } from './placeholderEmail';
import { useEmployeeLoginSwitch } from './useEmployeeLoginSwitch';

interface DeptRecord { id: string; name: string }
/**
 * 🔒 G3 — one row of `department_login_policy`, exactly as the SERVER reads it
 * (`GET /departments/login-policy` → `loadLoginPolicies`). The wizard used to
 * render a Role picker whose value the server discarded; it now displays the role
 * the chosen department will ACTUALLY grant, from the same rows that decide it.
 */
interface DeptLoginPolicy {
  departmentId: string;
  canOnboard: boolean;
  defaultRoleName: string | null;
  blockedReason?: string | null;
}
interface StagedDoc { docType: KycDocType; file: File; label: string }

interface PositionRecord { id: string; name: string; departmentId: string | null }
interface ChangeLogRow {
  id: string; field: string; oldValue: string | null; newValue: string | null;
  changedAt: string; actorName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /**
   * 2026-08-24 — absent = CREATE (unchanged behaviour). Present = EDIT: the same
   * five steps, prefilled from the record, saved with PUT.
   *
   * Manak's rule was explicit: the full wizard moves INTO Edit. The old
   * one-screen drawer is not the target and Add is not reduced to it.
   */
  employeeId?: string | null;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm max-sm:min-h-[40px] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

export function EmployeeOnboardingWizard({ open, onClose, onCreated, employeeId }: Props) {
  const mode: WizardMode = employeeId ? 'edit' : 'create';
  const [createdId, setCreatedId] = useState<string | null>(null);
  useEffect(() => { if (!open) setCreatedId(null); }, [open]);
  const [form, setForm] = useState<OnboardingForm>({ ...emptyOnboardingForm });
  const [stepIdx, setStepIdx] = useState(0);
  const [departments, setDepartments] = useState<DeptRecord[]>([]);
  const [deptPolicies, setDeptPolicies] = useState<DeptLoginPolicy[]>([]);
  const [positionRoles, setPositionRoles] = useState<{ departmentId: string; positionName: string; roleName: string }[]>([]);
  const [policyLoaded, setPolicyLoaded] = useState(false);
  const [docs, setDocs] = useState<StagedDoc[]>([]);
  const [positions, setPositions] = useState<PositionRecord[]>([]);
  const [existingDocTypes, setExistingDocTypes] = useState<string[]>([]);
  const [changeLog, setChangeLog] = useState<ChangeLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 2026-08-24 — the login switch. It used to live only in the EmployeesPage
  // drawer; the Edit pencil now opens THIS wizard, so the switch had to come
  // with it or HR would lose its only way to grant or revoke a login. Shared
  // hook, so the drawer and the wizard cannot drift apart. Create mode passes
  // `open: false` — the hook no-ops and never fetches for a row that does not
  // exist yet.
  const { loginState, loginBusy, loginError, toggleLogin } = useEmployeeLoginSwitch({
    employeeId: employeeId ?? null,
    open: open && mode === 'edit',
    onChanged: onCreated,
  });

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyOnboardingForm });
    setStepIdx(0);
    setDocs([]);
    setError(null);
    setDeptPolicies([]);
    setPositionRoles([]);
    setPolicyLoaded(false);
    setExistingDocTypes([]);
    setChangeLog([]);
    setPositions([]);
    // 🔒 `activeOnly` — the picker must not offer a department HR switched off in
    // Settings. The Settings screen itself omits the flag so it can still see them.
    api.get('/departments?activeOnly=true').then((r) => setDepartments(r.data.data ?? [])).catch(() => {});
    api
      .get('/departments/login-policy')
      .then((r) => { setDeptPolicies(r.data.data ?? []); setPositionRoles(r.data.positionRoles ?? []); })
      .catch(() => {})
      // 🔒 Loaded-or-failed either way: on failure the wizard says "the department
      // decides" rather than silently claiming a role it could not read.
      .finally(() => setPolicyLoaded(true));

    // EDIT — prefill from the record. Nothing below runs in create mode.
    if (!employeeId) return;
    setLoading(true);
    api
      .get(`/employees/${employeeId}`)
      .then((r) => {
        const e = r.data?.data ?? {};
        // Dates arrive as ISO; the <input type="date"> wants yyyy-mm-dd.
        const day = (v: unknown) => (v ? String(v).slice(0, 10) : '');
        setForm({
          ...emptyOnboardingForm,
          firstName: e.firstName ?? '',
          lastName: e.lastName ?? '',
          email: e.email ?? '',
          // 🔒 NEVER prefill the password. The server stores a hash, there is
          // nothing to show, and a masked placeholder would be saved back as a
          // literal new password. Blank means "leave it alone" (see validateStep).
          password: '',
          employeeCode: e.employeeCode ?? '',
          phone: e.phone ?? '',
          designation: e.designation ?? '',
          departmentId: e.departmentId ?? '',
          dateOfJoining: day(e.dateOfJoining),
          salary: e.salary != null ? String(e.salary) : '',
          dateOfBirth: day(e.dateOfBirth),
          gender: e.gender ?? '',
          maritalStatus: e.maritalStatus ?? '',
          bloodGroup: e.bloodGroup ?? '',
          // PAN lives in `pan` with `panNumber` as a legacy mirror; Aadhaar's full
          // number is in `aadharNumber` (one 'a' — the column really is spelled
          // that way). Read both so older rows still prefill.
          pan: e.pan ?? e.panNumber ?? '',
          aadhaarNumber: e.aadharNumber ?? e.aadhaarNumber ?? '',
          passportNumber: e.passportNumber ?? '',
          personalEmail: e.personalEmail ?? '',
          bankName: e.bankName ?? '',
          bankAccountNo: e.bankAccountNo ?? '',
          currentAddress: e.currentAddress ?? '',
          permanentAddress: e.permanentAddress ?? '',
          personToBeContacted: e.personToBeContacted ?? '',
          emergencyPhoneNumber: e.emergencyPhoneNumber ?? '',
          emergencyRelation: e.emergencyRelation ?? '',
          vehicleRegNo: e.vehicleRegNo ?? '',
          vehicleType: e.vehicleType ?? '',
          fuelType: e.fuelType ?? '',
        });
      })
      .catch(() => setError('Could not load this employee'))
      .finally(() => setLoading(false));

    api.get(`/employees/${employeeId}/kyc-documents`)
      .then((r) => setExistingDocTypes((r.data?.data ?? []).map((d: { docType: string }) => d.docType)))
      .catch(() => {});
    api.get(`/employees/${employeeId}/change-log`)
      .then((r) => setChangeLog(r.data?.data ?? []))
      .catch(() => {});
  }, [open, employeeId]);

  // Positions for the CHOSEN department (its own + the globals). Refetched on
  // every department change so the second dropdown always matches the first.
  useEffect(() => {
    if (!open || !form.departmentId) { setPositions([]); return; }
    api
      .get(`/hr-positions?activeOnly=true&departmentId=${encodeURIComponent(form.departmentId)}`)
      .then((r) => setPositions(r.data?.data ?? []))
      .catch(() => setPositions([]));
  }, [open, form.departmentId]);

  if (!open) return null;

  const step = ONBOARDING_STEPS[stepIdx];
  const stepErrors = validateStep(step.id as OnboardingStepId, form, mode);
  const isLast = stepIdx === ONBOARDING_STEPS.length - 1;
  const set = (k: keyof OnboardingForm, v: string) =>
    setForm((f) => {
      // Department FIRST, position SECOND (Manak, 2026-08-24). Changing the
      // department must drop a position that department does not offer, or the
      // record keeps a title that is no longer in its own dropdown.
      if (k === 'departmentId' && v !== f.departmentId) {
        const stillValid = positions.some(
          (p) => p.name === f.designation && (p.departmentId === v || p.departmentId === null),
        );
        return { ...f, departmentId: v, designation: stillValid ? f.designation : '' };
      }
      return { ...f, [k]: v };
    });

  // 🔒 What the SERVER will grant for the chosen department. Never a local guess.
  const chosenPolicy = deptPolicies.find((p) => p.departmentId === form.departmentId);
  const chosenDeptName = departments.find((d) => d.id === form.departmentId)?.name ?? '';
  const grantedRoleLabel = !form.departmentId
    ? 'Pick a department — it decides the role'
    : !policyLoaded
    ? 'Checking…'
    : chosenPolicy?.canOnboard && chosenPolicy.defaultRoleName
    ? (positionRoles.find((p) => p.departmentId === form.departmentId
        && p.positionName.trim().toLowerCase() === (form.designation ?? '').trim().toLowerCase())?.roleName
       ?? chosenPolicy.defaultRoleName)
    : deptPolicies.length === 0
    ? 'Decided by the department when the login is created'
    : `${chosenDeptName || 'This department'} cannot be given an AURA login`;

  const next = () => {
    if (stepErrors.length) return; // block advancing on invalid step
    setStepIdx((i) => Math.min(i + 1, ONBOARDING_STEPS.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const addDoc = (docType: KycDocType, file: File | null) => {
    if (!file) return;
    setDocs((d) => [...d, { docType, file, label: file.name }]);
  };
  const removeDoc = (i: number) => setDocs((d) => d.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!canSubmit(form, mode)) {
      setStepIdx(0);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let savedId: string;
      if (mode === 'edit') {
        // EDIT — PUT the record. `password` is only sent when HR actually typed
        // one; the route hashes it and writes an explicit set_password audit row.
        // Sending '' would be read as a deliberate reset to an empty password.
        const { password, aadhaarNumber, ...rest } = form;
        await api.put(`/employees/${employeeId}`, {
          ...rest,
          // The column is `aadharNumber` (one 'a'). The form field carries the
          // correct spelling, so translate on the way out rather than renaming
          // the form and breaking the create path.
          aadharNumber: aadhaarNumber || undefined,
          salary: form.salary === '' ? undefined : form.salary,
          ...(password.trim().length >= 8 ? { password } : {}),
        });
        savedId = employeeId as string;
      } else if (createdId) {
        // The employee was already created on an earlier Save (only some KYC
        // documents failed). Never create a second one: just retry the documents.
        savedId = createdId;
      } else {
        const res = await api.post('/employees/onboard', {
          ...form,
          salary: form.salary || undefined,
        });
        savedId = res.data?.data?.employee?.id;
        setCreatedId(savedId);
      }
      const employeeIdForDocs: string = savedId;
      // Upload + attach any staged KYC documents. The employee is already saved,
      // so a failed document must not lose them — but HR is told which ones failed.
      const failedDocs = await uploadKycDocs(docs, async (doc) => {
        const fd = new FormData();
        fd.append('file', doc.file);
        fd.append('entity', 'employee-kyc');
        fd.append('entityId', employeeIdForDocs);
        const up = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const fileUploadId: string = up.data?.data?.id;
        await api.post(`/employees/${employeeIdForDocs}/kyc-documents`, {
          docType: doc.docType,
          fileUploadId,
          label: doc.label,
        });
      });
      onCreated();
      if (failedDocs.length > 0) {
        setDocs(failedDocs);
        setError(
          `Employee saved, but ${failedDocs.length} document${failedDocs.length === 1 ? ' was' : 's were'} NOT uploaded: ${failedDocs
            .map((d) => d.label)
            .join(', ')}. Press Save again to retry only those.`,
        );
        return;
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to onboard employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      className="flex flex-col !max-w-2xl !overflow-hidden !bg-white max-sm:!h-full max-sm:!max-h-none max-sm:!max-w-none max-sm:!rounded-none"
    >
      <>
        {/* Header + step rail */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit employee' : 'Onboard employee'}
          </h2>
          <CloseButton className="text-gray-400 hover:text-gray-600 max-sm:-mr-2 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"><X size={18} /></CloseButton>
        </div>
        <div className="flex flex-wrap gap-1 border-b border-gray-100 px-5 py-3 max-sm:flex-nowrap max-sm:overflow-x-auto">
          {ONBOARDING_STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => (i < stepIdx || stepErrors.length === 0 ? setStepIdx(i) : undefined)}
              className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs max-sm:min-h-[40px] max-sm:shrink-0 max-sm:whitespace-nowrap ${
                i === stepIdx
                  ? 'bg-[var(--ink-0)] text-white'
                  : i < stepIdx
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {i < stepIdx && <Check size={12} />}
              {s.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step.id === 'identity' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>First name *</label><input className={inputCls} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></div>
              <div><label className={labelCls}>Last name *</label><input className={inputCls} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} /></div>
              <div>
                <label className={labelCls}>
                  Login email *
                  {isPlaceholderEmail(form.email) && (
                    <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">placeholder</span>
                  )}
                </label>
                <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
                {mode === 'create' && !form.email.trim() && /[a-z]/i.test(form.firstName) && (
                  <button
                    type="button"
                    onClick={() => set('email', placeholderEmailFor(form.firstName, []))}
                    className="mt-1 text-xs text-[var(--ink-0)] hover:underline"
                  >
                    Use placeholder {placeholderEmailFor(form.firstName, [])}
                  </button>
                )}
              </div>
              <div>
                <label className={labelCls}>
                  {mode === 'edit' ? 'Reset password' : 'Temporary password *'}
                </label>
                <input className={inputCls} type="text" value={form.password} onChange={(e) => set('password', e.target.value)} />
                {mode === 'edit' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Leave blank to keep the current password. Typing one resets it.
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>Department *</label>
                <select className={inputCls} value={form.departmentId} onChange={(e) => set('departmentId', e.target.value)}>
                  <option value="">Select department…</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              {/* 🔒 G3 — NOT a picker. The department's login policy decides the
                  role server-side; this shows what that will be. */}
              <div>
                <label className={labelCls}>Login role</label>
                <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {grantedRoleLabel}
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  Set by the department, not chosen here.
                </p>
              </div>
              <div><label className={labelCls}>Employee code</label><input className={inputCls} value={form.employeeCode} onChange={(e) => set('employeeCode', e.target.value)} /></div>
              {/* Department first, Position second — the list is empty until a
                  department is chosen, and only ever offers that department's
                  positions plus the global ones. Free text is gone on purpose:
                  it produced "Sales Executive", "sales exec" and "Sr. Sales
                  Executive" as three unrelated values. */}
              <div>
                <label className={labelCls}>Position</label>
                <select
                  className={inputCls}
                  value={form.designation}
                  disabled={!form.departmentId}
                  onChange={(e) => set('designation', e.target.value)}
                >
                  <option value="">
                    {!form.departmentId ? 'Pick a department first' : 'Select position…'}
                  </option>
                  {/* A position saved before this list existed (or one whose
                      department changed) must still be visible, or opening Edit
                      would silently blank it on the next save. */}
                  {form.designation && !positions.some((p) => p.name === form.designation) && (
                    <option value={form.designation}>{form.designation} (not in list)</option>
                  )}
                  {positions.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}{p.departmentId === null ? ' · any department' : ''}
                    </option>
                  ))}
                </select>
                {form.departmentId && positions.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    No positions yet for this department — add them in Settings &rarr; Departments &amp; Positions.
                  </p>
                )}
              </div>
              <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
              <div><label className={labelCls}>Date of joining</label><input className={inputCls} type="date" value={form.dateOfJoining} onChange={(e) => set('dateOfJoining', e.target.value)} /></div>
              <div><label className={labelCls}>Monthly salary (₹)</label><input className={inputCls} type="number" value={form.salary} onChange={(e) => set('salary', e.target.value)} /></div>
              {/* Edit only. On create there is no employee row yet to attach a
                  login to — the login is minted by POST /employees/onboard from
                  the password field above. `loginState === null` also covers the
                  403 case and the still-loading window: render NO control rather
                  than one the caller cannot use, or one showing the previous
                  person's state. */}
              {mode === 'edit' && loginState && (
                <div className="rounded-lg border border-gray-200 p-3 sm:col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={loginState.isActive}
                      disabled={loginBusy || (!loginState.hasLogin && !loginState.canOnboard)}
                      onChange={(e) => void toggleLogin(e.target.checked, { email: form.email, password: form.password })}
                    />
                    <span className="text-sm font-medium text-gray-700">This person can sign in to AURA</span>
                    {loginBusy && <Loader2 size={14} className="animate-spin text-gray-400" />}
                  </label>
                  {!loginState.canOnboard && !loginState.hasLogin && (
                    <p className="mt-2 text-sm text-amber-700">{loginState.blockedReason}</p>
                  )}
                  {loginState.canOnboard && !loginState.hasLogin && (
                    <p className="mt-2 text-xs text-gray-500">
                      Will be created as {loginState.defaultRoleName}. Set a password above first.
                    </p>
                  )}
                  {loginError && <p className="mt-2 text-xs text-red-700">{loginError}</p>}
                </div>
              )}
            </div>
          )}

          {step.id === 'personal' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Date of birth</label><input className={inputCls} type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} /></div>
              <div><label className={labelCls}>Gender</label><input className={inputCls} value={form.gender} onChange={(e) => set('gender', e.target.value)} /></div>
              <div><label className={labelCls}>Marital status</label><input className={inputCls} value={form.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)} /></div>
              <div><label className={labelCls}>Blood group</label><input className={inputCls} value={form.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)} /></div>
              <div><label className={labelCls}>PAN</label><input className={inputCls} value={form.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} placeholder="ABCPK1234F" /></div>
              <div><label className={labelCls}>Aadhaar number</label><input className={inputCls} value={form.aadhaarNumber} onChange={(e) => set('aadhaarNumber', e.target.value)} /></div>
              <div><label className={labelCls}>Passport number</label><input className={inputCls} value={form.passportNumber} onChange={(e) => set('passportNumber', e.target.value)} /></div>
              <div><label className={labelCls}>Personal email</label><input className={inputCls} type="email" value={form.personalEmail} onChange={(e) => set('personalEmail', e.target.value)} /></div>
            </div>
          )}

          {step.id === 'bank' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Bank name</label><input className={inputCls} value={form.bankName} onChange={(e) => set('bankName', e.target.value)} /></div>
              <div><label className={labelCls}>Account number</label><input className={inputCls} value={form.bankAccountNo} onChange={(e) => set('bankAccountNo', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Current address</label><textarea className={inputCls} rows={2} value={form.currentAddress} onChange={(e) => set('currentAddress', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Permanent address</label><textarea className={inputCls} rows={2} value={form.permanentAddress} onChange={(e) => set('permanentAddress', e.target.value)} /></div>
              <div><label className={labelCls}>Emergency contact name</label><input className={inputCls} value={form.personToBeContacted} onChange={(e) => set('personToBeContacted', e.target.value)} /></div>
              <div><label className={labelCls}>Emergency phone</label><input className={inputCls} value={form.emergencyPhoneNumber} onChange={(e) => set('emergencyPhoneNumber', e.target.value)} /></div>
              <div><label className={labelCls}>Emergency relation</label><input className={inputCls} value={form.emergencyRelation} onChange={(e) => set('emergencyRelation', e.target.value)} /></div>
            </div>
          )}

          {step.id === 'vehicle' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <p className="sm:col-span-2 text-xs text-gray-500">For fuel &amp; Vehicle-maintenance reimbursement (flat ₹/day TA/DA — no odometer/GPS).</p>
              <div><label className={labelCls}>Vehicle reg. no.</label><input className={inputCls} value={form.vehicleRegNo} onChange={(e) => set('vehicleRegNo', e.target.value.toUpperCase())} placeholder="HR06 AZ 1234" /></div>
              <div>
                <label className={labelCls}>Vehicle type</label>
                <select className={inputCls} value={form.vehicleType} onChange={(e) => set('vehicleType', e.target.value)}>
                  <option value="">—</option>
                  <option value="car">Car</option>
                  <option value="bike">Bike</option>
                  <option value="scooter">Scooter</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Fuel type</label>
                <select className={inputCls} value={form.fuelType} onChange={(e) => set('fuelType', e.target.value)}>
                  <option value="">—</option>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="cng">CNG</option>
                  <option value="ev">EV</option>
                </select>
              </div>
            </div>
          )}

          {step.id === 'documents' && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {KYC_DOC_TYPES.map((t) => (
                  <label key={t} className="flex cursor-pointer items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs hover:bg-gray-50">
                    <Upload size={13} /> {kycDocTypeLabel(t)}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,application/pdf"
                      onChange={(e) => { addDoc(t, e.target.files?.[0] ?? null); e.currentTarget.value = ''; }}
                    />
                  </label>
                ))}
              </div>
              {docs.length === 0 && <p className="text-xs text-gray-500">No documents staged. Uploads are optional; each starts as "pending" and can be verified later.</p>}
              <ul className="space-y-1">
                {docs.map((d, i) => (
                  <li key={i} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <span><span className="font-medium">{kycDocTypeLabel(d.docType)}</span> — {d.label}</span>
                    <button onClick={() => removeDoc(i)} className="text-gray-400 hover:text-red-600 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:shrink-0 max-sm:items-center max-sm:justify-center"><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
              {/* Documents ALREADY on the record. Without this, Edit looks like the
                  person has uploaded nothing and HR re-uploads what is already there. */}
              {mode === 'edit' && existingDocTypes.length > 0 && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="mb-2 text-xs font-medium text-gray-700">Already on file</p>
                  <div className="flex flex-wrap gap-1">
                    {existingDocTypes.map((t, i) => (
                      <span key={`${t}-${i}`} className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                Review: <span className="font-medium">{form.firstName} {form.lastName}</span> · {form.email} · {chosenDeptName || 'no department'} · login role {grantedRoleLabel}
              </div>

              {/* History — what has been edited, who did it, when. Edit only:
                  a record being created has no history yet. */}
              {mode === 'edit' && (
                <div className="rounded-lg border border-gray-200">
                  <p className="border-b border-gray-100 px-3 py-2 text-xs font-medium text-gray-700">
                    History{changeLog.length > 0 ? ` · ${changeLog.length} change${changeLog.length === 1 ? '' : 's'}` : ''}
                  </p>
                  {changeLog.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-500">No edits recorded yet.</p>
                  ) : (
                    <ul className="max-h-48 overflow-y-auto">
                      {changeLog.map((c) => (
                        <li key={c.id} className="border-b border-gray-50 px-3 py-2 text-xs last:border-0">
                          <span className="font-medium text-gray-800">{c.field}</span>
                          {': '}
                          <span className="text-gray-500">{c.oldValue ?? '(blank)'}</span>
                          {' → '}
                          <span className="text-gray-800">{c.newValue ?? '(blank)'}</span>
                          <span className="ml-1 text-gray-400">
                            · {c.actorName} · {new Date(c.changedAt).toLocaleString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {stepErrors.length > 0 && (
            <ul className="mt-3 list-disc rounded-lg bg-red-50 px-6 py-2 text-xs text-red-700">
              {stepErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
          {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
          <button onClick={back} disabled={stepIdx === 0} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-40 max-sm:min-h-[44px]">
            <ChevronLeft size={14} /> Back
          </button>
          {isLast ? (
            <button onClick={submit} disabled={saving || loading || !canSubmit(form, mode)} className="btn btn-primary flex items-center gap-1 disabled:opacity-50 max-sm:min-h-[44px]">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {mode === 'edit' ? 'Save changes' : 'Create employee'}
            </button>
          ) : (
            <button onClick={next} disabled={stepErrors.length > 0} className="btn btn-primary flex items-center gap-1 disabled:opacity-50 max-sm:min-h-[44px]">
              Next <ChevronRight size={14} />
            </button>
          )}
        </div>
      </>
    </Modal>
  );
}

function CloseButton({ className, children }: { className?: string; children: React.ReactNode }) {
  const { requestClose } = useModalClose();
  return <button type="button" onClick={requestClose} className={className}>{children}</button>;
}
